import React, { useState, useEffect } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material';

const FALLBACK_IMAGE = '/assets/placeholder-equipment.svg';

const ImageCarousel = ({ 
  images = [], 
  alt = "Image", 
  height = 120, 
  width = "100%", 
  showControls = true,
  showIndicators = true,
  showCounter = true,
  autoPlay = false,
  autoPlayInterval = 3000,
  onImageChange,
  sx: externalSx,
  ...restProps 
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  // Préparation des images
  let processedImages = [];
  
  if (images.length === 0) {
    // Aucune image fournie, utiliser un placeholder simple
    processedImages = [FALLBACK_IMAGE];
    console.log('ℹ️ Aucune image fournie, utilisation d\'un placeholder');
  } else {
    // Vérifier que toutes les images sont des chaînes de caractères
    const validImages = images.filter(img => typeof img === 'string');
    
    if (validImages.length === 0) {
      console.warn('⚠️ Aucune image valide n\'a été trouvée. Format attendu: chaîne de caractères', images);
      processedImages = [FALLBACK_IMAGE];
    } else {
      // Utiliser les images fournies en supprimant les doublons
      processedImages = [...new Set(validImages)];
    }
  }

  // Debug temporaire
  console.log('🎠 ImageCarousel debug:', {
    originalImages: images.length,
    processedImages: processedImages.length,
    showControls,
    showIndicators,
    currentIndex,
    firstImage: processedImages[0],
    allProcessedImages: processedImages
  });

  // Auto-play functionality
  useEffect(() => {
    if (autoPlay && processedImages.length > 1 && !isHovered) {
      const interval = setInterval(() => {
        setCurrentIndex(prev => (prev + 1) % processedImages.length);
      }, autoPlayInterval);
      
      return () => clearInterval(interval);
    }
  }, [autoPlay, autoPlayInterval, processedImages.length, isHovered]);

  // Reset index when images change
  useEffect(() => {
    setCurrentIndex(0);
  }, [images]);

  // Notify parent of image changes
  useEffect(() => {
    if (onImageChange) {
      onImageChange(currentIndex, processedImages[currentIndex]);
    }
  }, [currentIndex, onImageChange, processedImages]);

  const goToPrevious = (e) => {
    if (e) e.stopPropagation();
    if (processedImages.length <= 1) {
      console.log('⬅️ Previous clicked but only one image, skipping');
      return;
    }
    
    console.log('⬅️ Previous clicked, current:', currentIndex, 'total images:', processedImages.length);
    setCurrentIndex(prev => {
      const newIndex = prev === 0 ? processedImages.length - 1 : prev - 1;
      console.log('⬅️ New index:', newIndex);
      return newIndex;
    });
  };

  const goToNext = (e) => {
    if (e) e.stopPropagation();
    if (processedImages.length <= 1) {
      console.log('➡️ Next clicked but only one image, skipping');
      return;
    }
    
    console.log('➡️ Next clicked, current:', currentIndex, 'total images:', processedImages.length);
    setCurrentIndex(prev => {
      const newIndex = (prev + 1) % processedImages.length;
      console.log('➡️ New index:', newIndex);
      return newIndex;
    });
  };

  const goToSlide = (index, e) => {
    e?.stopPropagation();
    setCurrentIndex(index);
  };

  // Don't render if no images
  if (processedImages.length === 0) {
    return (
      <Box
        sx={{
          width,
          height,
          backgroundColor: 'grey.200',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...externalSx
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Aucune image
        </Typography>
      </Box>
    );
  }

  // Afficher les contrôles de navigation seulement s'il y a plusieurs images ou si showControls est explicitement à true
  const showNavigation = showControls && (processedImages.length > 1 || restProps.alwaysShowControls === true);

  return (
    <Box
      sx={{
        position: 'relative',
        width,
        height,
        overflow: 'hidden',
        borderRadius: 2,
        backgroundColor: 'rgba(245, 247, 250, 0.02)',
        boxShadow: 'inset 0 0 10px rgba(0, 0, 0, 0.03)',
        '&:hover .carousel-control': {
          opacity: 1,
        },
        ...externalSx
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      {...restProps}
    >
      {/* Main Image */}
      <Box
        component="img"
        src={processedImages[currentIndex]}
        alt={`${alt} ${currentIndex + 1}`}
        key={`image-${currentIndex}-${processedImages[currentIndex]}`}
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center',
          transition: 'transform 0.3s ease',
          transform: isHovered ? 'scale(1.03)' : 'scale(1)',
          display: 'block',
        }}
        onLoad={() => {
          console.log('✅ Image loaded:', processedImages[currentIndex], 'Index:', currentIndex);
        }}
        onError={(e) => {
          // Avoid retry loops if the fallback itself fails.
          if (e.target.dataset.fallbackApplied === 'true') {
            return;
          }

          console.log('❌ Image failed to load:', e.target.src);
          e.target.dataset.fallbackApplied = 'true';
          e.target.src = FALLBACK_IMAGE;
        }}
      />

      {/* Navigation Arrows */}
      {showNavigation && (
        <>
          {/* Previous Button */}
          <IconButton
            onClick={goToPrevious}
            className="carousel-control"
            sx={{
              position: 'absolute',
              left: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: 'rgba(255, 255, 255, 0.9)', // Fond blanc avec légère transparence
              color: 'rgba(25, 118, 210, 0.9)', // Couleur primaire (bleu)
              width: 28,
              height: 28,
              opacity: 0.8,
              transition: 'all 0.2s ease',
              borderRadius: '50%',
              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 1)',
                color: 'rgba(25, 118, 210, 1)',
                transform: 'translateY(-50%) scale(1.1)',
                opacity: 1,
                boxShadow: '0 3px 8px rgba(0, 0, 0, 0.15)'
              },
              zIndex: 10,
            }}
          >
            <ArrowBackIos sx={{ fontSize: 14, ml: 0.5 }} />
          </IconButton>

          {/* Next Button */}
          <IconButton
            onClick={goToNext}
            className="carousel-control"
            sx={{
              position: 'absolute',
              right: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: 'rgba(255, 255, 255, 0.9)', // Fond blanc avec légère transparence
              color: 'rgba(25, 118, 210, 0.9)', // Couleur primaire (bleu)
              width: 28,
              height: 28,
              opacity: 0.8,
              transition: 'all 0.2s ease',
              borderRadius: '50%',
              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 1)',
                color: 'rgba(25, 118, 210, 1)',
                transform: 'translateY(-50%) scale(1.1)',
                opacity: 1,
                boxShadow: '0 3px 8px rgba(0, 0, 0, 0.15)'
              },
              zIndex: 10,
            }}
          >
            <ArrowForwardIos sx={{ fontSize: 14 }} />
          </IconButton>
        </>
      )}

      {/* Indicators */}
      {showIndicators && processedImages.length > 1 && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 10,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 0.75,
            backgroundColor: 'rgba(255, 255, 255, 0.85)', // Fond blanc avec légère transparence
            borderRadius: 20, // Très arrondi pour un look moderne
            px: 1,
            py: 0.5,
            zIndex: 2,
            boxShadow: '0 1px 4px rgba(0, 0, 0, 0.1)',
          }}
        >
          {processedImages.map((_, index) => (
            <Box
              key={index}
              onClick={(e) => goToSlide(index, e)}
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: 
                  index === currentIndex ? 'rgba(25, 118, 210, 0.9)' : 'rgba(0, 0, 0, 0.2)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: index === currentIndex ? 'rgba(25, 118, 210, 1)' : 'rgba(0, 0, 0, 0.4)',
                  transform: 'scale(1.2)',
                },
              }}
            />
          ))}
        </Box>
      )}

      {/* Counter */}
      {showCounter && processedImages.length > 1 && (
        <Box
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            backgroundColor: 'rgba(255, 255, 255, 0.85)',
            color: 'rgba(0, 0, 0, 0.7)',
            px: 0.8,
            py: 0.4,
            borderRadius: 4,
            fontSize: '0.7rem',
            fontWeight: 500,
            zIndex: 2,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          }}
        >
          {currentIndex + 1}/{processedImages.length}
        </Box>
      )}
    </Box>
  );
};

export default ImageCarousel;
